"use client";

import { usePostHogCapture } from "@/hooks/use-posthog-capture";
import {
  type AgentApiTransport,
  type ChatConfig,
  ChatConfigProvider,
} from "@medimo/chat/context";
import { fetchAuthSession } from "aws-amplify/auth";
import { type PropsWithChildren, useMemo } from "react";

// env / Amplify を読むのは配信面(この web ラッパ)だけ。共有チャット実体は config 経由で受け取る。
const AGENT_API_URL =
  process.env.NEXT_PUBLIC_AGENT_API_URL ?? "http://localhost:4111";

/** backend と同様に Cognito の id トークンを取得する。 */
async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error("認証情報が取得できませんでした");
  }
  return token;
}

export const webAgentApiTransport: AgentApiTransport = {
  agentApiUrl: AGENT_API_URL,
  chatPath: "/chat",
  threadsPath: "/chat/threads",
  getAuthToken: getIdToken,
};

/**
 * web(医師カルテ組込)配信面の ChatConfig を注入する Provider。
 * 医師 Cognito idToken と "/chat" 系ルートを与える。独立版・管理者 PE は
 * 各配信面が別の Provider で自分の config(認証源 / ルート)を注入する。
 */
export const WebChatConfigProvider = ({ children }: PropsWithChildren) => {
  // 計測基盤(PostHog)を読むのも配信面だけ。パッケージへは capture 関数として注入する。
  const capturePostHog = usePostHogCapture();
  const value = useMemo<ChatConfig>(
    () => ({
      ...webAgentApiTransport,
      captureAnalyticsEvent: capturePostHog,
    }),
    [capturePostHog],
  );
  return <ChatConfigProvider value={value}>{children}</ChatConfigProvider>;
};
